import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    { path: '/', name: 'landing', component: () => import('@/views/LandingView.vue') },
    { path: '/documentation', name: 'documentation', component: () => import('@/views/DocumentationView.vue') },
    { path: '/login', name: 'login', component: () => import('@/views/LoginView.vue'), meta: { guest: true } },
    { path: '/register', name: 'register', component: () => import('@/views/RegisterView.vue'), meta: { guest: true } },
    {
      path: '/super-admin',
      component: () => import('@/layouts/SuperAdminLayout.vue'),
      meta: { requiresAuth: true, superAdmin: true },
      children: [
        { path: '', redirect: { name: 'super-admin-dashboard' } },
        { path: 'dashboard', name: 'super-admin-dashboard', component: () => import('@/views/super-admin/SuperAdminDashboardView.vue') },
        { path: 'pharmacies', name: 'super-admin-pharmacies', component: () => import('@/views/super-admin/SuperAdminPharmaciesView.vue') },
        { path: 'pharmacies/:pharmacySlug', name: 'super-admin-pharmacy', component: () => import('@/views/super-admin/SuperAdminPharmacyView.vue') },
        { path: 'subscriptions', name: 'super-admin-subscriptions', component: () => import('@/views/super-admin/SuperAdminSubscriptionsView.vue') },
        { path: 'payments', name: 'super-admin-payments', component: () => import('@/views/super-admin/SuperAdminPaymentsView.vue') },
        { path: 'payment-providers', name: 'super-admin-payment-providers', component: () => import('@/views/super-admin/SuperAdminPaymentProvidersView.vue') },
        { path: 'plans', name: 'super-admin-plans', component: () => import('@/views/super-admin/SuperAdminPlansView.vue') },
        { path: 'users', name: 'super-admin-users', component: () => import('@/views/super-admin/SuperAdminUsersView.vue') },
        { path: 'audit-logs', name: 'super-admin-audit', component: () => import('@/views/super-admin/SuperAdminAuditView.vue') },
        { path: 'support', name: 'super-admin-support', component: () => import('@/views/super-admin/SuperAdminSupportView.vue') },
        { path: 'platform-settings', name: 'super-admin-platform-settings', component: () => import('@/views/super-admin/SuperAdminPlatformSettingsView.vue') },
        { path: 'profile', name: 'super-admin-profile', component: () => import('@/views/ProfileView.vue') },
      ],
    },
    {
      path: '/:pharmacySlug',
      component: () => import('@/layouts/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        { path: '', redirect: { name: 'dashboard' } },
        { path: 'dashboard', name: 'dashboard', component: () => import('@/views/DashboardView.vue') },
        { path: 'users', name: 'users', component: () => import('@/views/UsersView.vue'), meta: { admin: true } },
        { path: 'medicines', name: 'medicines', component: () => import('@/views/MedicinesView.vue') },
        { path: 'stock', name: 'stock', component: () => import('@/views/StockView.vue') },
        { path: 'stock-cards', name: 'stock-cards', component: () => import('@/views/StockCardsView.vue') },
        { path: 'inventories', name: 'inventories', component: () => import('@/views/InventoriesView.vue') },
        { path: 'suppliers', name: 'suppliers', component: () => import('@/views/SuppliersView.vue') },
        { path: 'sites', name: 'sites', component: () => import('@/views/SitesView.vue'), meta: { admin: true } },
        { path: 'pos', name: 'pos', component: () => import('@/views/PosView.vue') },
        { path: 'sales', name: 'sales', component: () => import('@/views/SalesView.vue') },
        { path: 'credits', name: 'credits', component: () => import('@/views/CreditSalesView.vue') },
        { path: 'customers', name: 'customers', component: () => import('@/views/CustomersView.vue'), meta: { professional: true } },
        { path: 'finance', name: 'finance', component: () => import('@/views/FinanceView.vue') },
        { path: 'reports', name: 'reports', component: () => import('@/views/ReportsView.vue'), meta: { admin: true } },
        { path: 'settings', name: 'pharmacy-settings', component: () => import('@/views/PharmacySettingsView.vue'), meta: { admin: true } },
        { path: 'profile', name: 'profile', component: () => import('@/views/ProfileView.vue') },
        { path: 'support', name: 'support', component: () => import('@/views/SupportChatView.vue') },
        { path: 'receipt-settings', name: 'receipt-settings', component: () => import('@/views/ReceiptSettingsView.vue'), meta: { admin: true } },
        { path: 'subscription', name: 'subscription', component: () => import('@/views/SubscriptionView.vue'), meta: { admin: true } },
        { path: 'audit-logs', name: 'tenant-audit', component: () => import('@/views/TenantAuditView.vue'), meta: { admin: true } },
        { path: 'payroll', name: 'payroll', component: () => import('@/views/PayrollView.vue'), meta: { admin: true } },
      ],
    },
    { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFoundView.vue') },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  if (!auth.initialized) await auth.fetchMe()

  if (to.meta.requiresAuth && !auth.authenticated) return { name: 'login', query: { redirect: to.fullPath } }
  if (to.meta.guest && auth.authenticated) {
    if (auth.isSuperAdmin) return { name: 'super-admin-dashboard' }
    const slug = auth.memberships[0]?.pharmacy?.slug
    if (slug) return { name: 'dashboard', params: { pharmacySlug: slug } }
  }
  if (to.meta.superAdmin && !auth.isSuperAdmin) {
    const fallback = auth.memberships[0]?.pharmacy?.slug
    return fallback ? { name: 'dashboard', params: { pharmacySlug: fallback } } : { name: 'login' }
  }
  if (to.meta.requiresAuth) {
    if (to.meta.superAdmin) return true
    const slug = String(to.params.pharmacySlug || '')
    const membership = auth.membershipFor(slug)
    if (!membership) {
      const fallback = auth.memberships[0]?.pharmacy?.slug
      return fallback ? { name: 'dashboard', params: { pharmacySlug: fallback } } : { name: 'login' }
    }
    if (to.meta.admin && !['tenant_admin', 'pharmacy_manager'].includes(membership.role)) {
      return { name: 'dashboard', params: { pharmacySlug: slug } }
    }
  }
})

window.addEventListener('auth:expired', () => void router.push({ name: 'login' }))

export default router
